import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Grid, Chip, Skeleton, Divider, Alert, Snackbar,
  Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction, CircularProgress, TextField, Tabs, Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Edit as EditIcon, Cancel as CancelIcon, Share as ShareIcon,
  Notifications as NotificationsIcon, NotificationsNone as NotificationsNoneIcon,
  Person as PersonIcon, CalendarToday, People, AccessTime, CurrencyRuble, LocationCity,
  TransitEnterexit, DirectionsSubway, Event as EventIcon, Chat as ChatIcon,
  ContentCopy as ContentCopyIcon, Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon, Block as BlockIcon, LockOpen as LockOpenIcon, Lock as LockIcon,
} from '@mui/icons-material';
import { eventsAPI, type Event } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ReCaptchaWidget from '../components/ReCaptchaWidget';

interface Participant { id: number; first_name: string | null; last_name: string | null; username?: string | null; joined_at?: string | null; }
interface Application { id: number; event_id: number; user_id: number; username: string | null; first_name: string | null; last_name: string | null; created_at: string; }

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [joinCaptchaRequired, setJoinCaptchaRequired] = useState(false);
  const [joinCaptchaResetKey, setJoinCaptchaResetKey] = useState(0);

  // Organizer management state
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtTab, setMgmtTab] = useState(0);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');

  const isOrganizer = event?.user_role === 'organizer';

  const loadEventData = async () => {
    try {
      const eventData = await eventsAPI.getEvent(Number(id));
      setEvent(eventData);
      setSubscribed(eventData.user_subscription);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Не удалось загрузить мероприятие');
    }
  };

  const loadManagementData = async () => {
    if (!isOrganizer) return;
    try {
      setMgmtLoading(true);
      const [pData, aData] = await Promise.all([
        eventsAPI.getParticipants(Number(id)),
        eventsAPI.getApplications(Number(id)),
      ]);
      setParticipants([...pData].sort((a, b) => {
        const nA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
        const nB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
        return nA.localeCompare(nB, 'ru');
      }));
      setApplications(aData);
    } catch (e) { console.error(e); }
    finally { setMgmtLoading(false); }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadEventData();
      setLoading(false);
    };
    init();
  }, [id]);

  useEffect(() => {
    if (isOrganizer) loadManagementData();
  }, [isOrganizer, id]);

  const reloadAll = async () => {
    await loadEventData();
    if (isOrganizer) await loadManagementData();
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => setSnackbar({ open: true, message, severity });
  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => setConfirmDialog({ open: true, title, message, onConfirm });

  const handleJoinEvent = async (captchaToken?: string) => {
    if (!isAuthenticated) { navigate('/login', { state: { from: { pathname: `/events/${id}` } } }); return; }
    try {
      const result = await eventsAPI.joinEvent(Number(id), captchaToken);
      if (result?.captcha_required) { setJoinCaptchaRequired(true); showSnackbar(result.message || 'Требуется CAPTCHA', 'info'); return; }
      setJoinCaptchaRequired(false);
      showSnackbar(result.message, 'success');
      await reloadAll();
    } catch (err: any) {
      showSnackbar(err.response?.data?.detail || 'Ошибка при записи', 'error');
      if (joinCaptchaRequired) setJoinCaptchaResetKey(k => k + 1);
    }
  };

  const handleLeaveEvent = () => {
    showConfirmDialog('Отмена записи', 'Отменить запись на мероприятие?', async () => {
      try { const r = await eventsAPI.leaveEvent(Number(id)); showSnackbar(r.message, 'success'); await reloadAll(); }
      catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
    });
  };

  const handleCancelApplication = () => {
    showConfirmDialog('Отмена заявки', 'Отменить заявку?', async () => {
      try { const r = await eventsAPI.cancelApplication(Number(id)); showSnackbar(r.message, 'success'); await reloadAll(); }
      catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
    });
  };

  const handleSubscribe = async () => {
    try {
      if (subscribed) { await eventsAPI.unsubscribeFromEvent(Number(id)); setSubscribed(false); showSnackbar('Подписка отменена', 'info'); }
      else { await eventsAPI.subscribeToEvent(Number(id)); setSubscribed(true); showSnackbar('Уведомления включены', 'success'); }
    } catch { showSnackbar('Ошибка подписки', 'error'); }
  };

  const handleShare = async () => {
    if (!event) return;
    const date = new Date(event.start_datetime);
    const dateStr = date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    try { await navigator.clipboard.writeText(`${event.title} ${dateStr} ${window.location.href}`); showSnackbar('Скопировано!', 'success'); }
    catch { showSnackbar('Не удалось скопировать', 'error'); }
  };

  // Organizer management handlers
  const handleChangeStatus = async (status: 'open' | 'closed' | 'cancelled') => {
    const labels: Record<string, string> = { open: 'открыть регистрацию', closed: 'закрыть регистрацию', cancelled: 'отменить мероприятие' };
    showConfirmDialog('Подтверждение', `Вы уверены, что хотите ${labels[status]}?`, async () => {
      try { await eventsAPI.changeEventStatus(Number(id), status); showSnackbar('Статус изменён', 'success'); await reloadAll(); }
      catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
    });
  };

  const handleApproveApplication = async (applicationId: number) => {
    try { await eventsAPI.approveApplication(applicationId); showSnackbar('Заявка одобрена', 'success'); await reloadAll(); }
    catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
  };

  const handleRejectApplication = async (applicationId: number) => {
    showConfirmDialog('Отклонение', 'Отклонить заявку?', async () => {
      try { await eventsAPI.rejectApplication(applicationId); showSnackbar('Заявка отклонена', 'success'); await reloadAll(); }
      catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
    });
  };

  const handleRemoveParticipant = async (participantId: number) => {
    showConfirmDialog('Удаление', 'Удалить участника?', async () => {
      try { await eventsAPI.removeParticipant(Number(id), participantId); showSnackbar('Участник удалён', 'success'); await reloadAll(); }
      catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
    });
  };

  const handleSendReminder = async () => {
    try { await eventsAPI.sendReminder(Number(id), reminderMsg || undefined); setReminderDialog(false); setReminderMsg(''); showSnackbar('Напоминание отправлено', 'success'); }
    catch (err: any) { showSnackbar(err.response?.data?.detail || 'Ошибка', 'error'); }
  };

  const formatDate = (s: string) => new Date(s).toLocaleString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatDateShort = (s: string) => new Date(s).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const getStatusText = (s: string) => s === 'open' ? 'Набор открыт' : s === 'closed' ? 'Набор завершен' : 'Отменено';
  const getUserRoleText = (r: string) => r === 'organizer' ? 'Вы организатор' : r === 'participant' ? 'Вы участник' : r === 'applicant' ? 'Заявка на рассмотрении' : '';
  const getUserRoleColor = (r: string) => r === 'organizer' ? 'info' : r === 'participant' ? 'success' : r === 'applicant' ? 'warning' : 'default';
  const getUserName = (p: { first_name: string | null; last_name: string | null; username?: string | null }) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.username || 'Пользователь';

  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mb: 2 }}>Назад</Button>
        <Paper sx={{ p: 3 }}><Skeleton variant="text" height={40} sx={{ mb: 1 }} /><Skeleton variant="rectangular" height={120} sx={{ mb: 1 }} /><Skeleton variant="text" /><Skeleton variant="text" /></Paper>
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mb: 2 }}>Назад</Button>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Мероприятие не найдено'}</Alert>
        <Button variant="contained" onClick={() => navigate('/events')}>К списку</Button>
      </Box>
    );
  }

  const participantsCount = event.reserved_seats + event.current_participants;
  const isFull = participantsCount >= event.max_participants;

  const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
      <Box sx={{ color: '#999', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
        <Typography variant="body2" fontWeight={600}>{value}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Top nav */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} size="small">Назад</Button>
        <Button size="small" variant="text" endIcon={<ShareIcon fontSize="small" />} onClick={handleShare}
          sx={{ fontSize: '0.75rem', color: '#64748b' }}>
          Поделиться
        </Button>
      </Box>

      {/* Header with title + status */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip label={getStatusText(event.status)} size="small" sx={{
            fontWeight: 600,
            ...(event.status === 'open' && { bgcolor: '#e8f5e9', color: '#2e7d32' }),
            ...(event.status === 'closed' && { bgcolor: '#fff3e0', color: '#ef6c00' }),
            ...(event.status === 'cancelled' && { bgcolor: '#f5f5f5', color: '#616161' }),
          }} />
          <Chip label={event.is_public ? 'Открытое' : 'Закрытое'} variant="outlined" size="small" sx={{
            ...(event.is_public ? { borderColor: '#2e7d32', color: '#2e7d32' } : { borderColor: '#ef6c00', color: '#ef6c00' }),
          }} />
          {event.user_role !== 'guest' && <Chip label={getUserRoleText(event.user_role)} color={getUserRoleColor(event.user_role) as any} size="small" />}
        </Box>
        <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.6rem' }, lineHeight: 1.25 }}>
          {event.sport_type_icon || '🎯'} {event.title}
        </Typography>

        {/* Organizer management buttons under title */}
        {isOrganizer && (
          <Box sx={{ mt: 2 }}>
            {/* Row 1: Чат, Напоминание, Редактировать */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
              <Button size="small" variant="outlined" startIcon={<ChatIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                onClick={() => navigate(`/chats/${id}`)} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                Чат
              </Button>
              <Button size="small" variant="outlined" startIcon={<NotificationsIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                onClick={() => setReminderDialog(true)} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}
                disabled={participants.length === 0 || event.status === 'cancelled'}>
                Напоминание
              </Button>
              {event.status !== 'cancelled' && (
                <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={() => navigate(`/events/${id}/edit`)} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Редактировать
                </Button>
              )}
            </Box>
            {/* Row 2: Закрыть/Открыть, Отменить */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {event.status === 'open' && (
                <Button size="small" variant="outlined" color="warning" startIcon={<LockIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={() => handleChangeStatus('closed')} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Закрыть регистрацию
                </Button>
              )}
              {event.status === 'closed' && (
                <Button size="small" variant="outlined" color="success" startIcon={<LockOpenIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={() => handleChangeStatus('open')} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Открыть регистрацию
                </Button>
              )}
              {event.status !== 'cancelled' && (
                <Button size="small" variant="outlined" color="error" startIcon={<BlockIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={() => handleChangeStatus('cancelled')} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Отменить мероприятие
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Participant/guest buttons under title */}
        {!isOrganizer && (
          <Box sx={{ mt: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {event.user_role === 'participant' && (
              <>
                <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={handleLeaveEvent} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Отменить запись
                </Button>
                <Button size="small" variant="outlined" startIcon={<ChatIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                  onClick={() => navigate(`/chats/${id}`)} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                  Чат мероприятия
                </Button>
              </>
            )}
            {event.user_role === 'applicant' && (
              <Button size="small" variant="outlined" color="warning" startIcon={<CancelIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                onClick={handleCancelApplication} sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 } }}>
                Отменить заявку
              </Button>
            )}
            {event.can_join && !isFull && event.status === 'open' && (
              <Button size="small" variant="contained" startIcon={<EventIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
                onClick={() => handleJoinEvent()}
                sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, py: { xs: 0.15, sm: 0.25 }, px: { xs: 0.5, sm: 1 }, bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>
                {event.is_public ? 'Записаться' : 'Подать заявку'}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Grid container spacing={2}>
        {/* Main info */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>Информация</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<CalendarToday sx={{ fontSize: 18 }} />} label="Дата и время" value={formatDate(event.start_datetime)} />
                <InfoRow icon={<LocationCity sx={{ fontSize: 18 }} />} label="Город" value={event.city} />
                {event.district && <InfoRow icon={<TransitEnterexit sx={{ fontSize: 18 }} />} label="Район" value={event.district} />}
                {event.metro_station && <InfoRow icon={<DirectionsSubway sx={{ fontSize: 18 }} />} label="Метро" value={`м. ${event.metro_station}`} />}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<CurrencyRuble sx={{ fontSize: 18 }} />} label="Стоимость" value={event.price > 0 ? `${event.price} руб.` : 'Бесплатно'} />
                <InfoRow icon={<People sx={{ fontSize: 18 }} />} label="Участники" value={`${participantsCount} из ${event.max_participants}${event.min_participants > 1 ? ` (мин. ${event.min_participants})` : ''}`} />
                {event.reserved_seats > 0 && <InfoRow icon={<AccessTime sx={{ fontSize: 18 }} />} label="Зарезервировано" value={`${event.reserved_seats}`} />}
                {event.applications_count !== undefined && event.applications_count > 0 && <InfoRow icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Заявки" value={`${event.applications_count}`} />}
              </Grid>
            </Grid>

            {(event.user_role === 'participant' || event.user_role === 'organizer') && (
              <>
                <Divider sx={{ my: 2 }} />
                {event.exact_address && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Точный адрес</Typography>
                    <Typography variant="body2" fontWeight={600}>{event.exact_address}</Typography>
                  </Box>
                )}
                {event.meeting_place && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Место встречи</Typography>
                    <Typography variant="body2" fontWeight={600}>{event.meeting_place}</Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Description */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Описание</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {event.description || 'Описание отсутствует'}
            </Typography>
          </Paper>

          {/* Organizer: Participants & Applications under description */}
          {isOrganizer && (
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
              <Tabs value={mgmtTab} onChange={(_, v) => setMgmtTab(v)} variant="fullWidth"
                sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontWeight: 700, fontSize: '0.8rem', py: 0 } }}>
                <Tab label={`Участники (${participants.length})`} />
                <Tab label={`Заявки (${applications.length})`} />
              </Tabs>
              <Divider sx={{ mb: 1.5 }} />

              {mgmtLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : mgmtTab === 0 ? (
                /* Participants tab */
                participants.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>Нет участников</Typography>
                ) : (
                  <List sx={{ py: 0 }}>
                    {participants.map(p => (
                      <ListItem key={p.id} divider sx={{ px: 0, py: 0.75 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>{getUserName(p)[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{getUserName(p)}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{[p.username ? `@${p.username}` : '', p.joined_at ? formatDateShort(p.joined_at) : ''].filter(Boolean).join(' · ')}</Typography>}
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" color="error" onClick={() => handleRemoveParticipant(p.id)} title="Удалить"><DeleteIcon fontSize="small" /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )
              ) : (
                /* Applications tab */
                applications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>Нет заявок</Typography>
                ) : (
                  <List sx={{ py: 0 }}>
                    {applications.map(app => (
                      <ListItem key={app.id} divider sx={{ px: 0, py: 0.75 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>{getUserName(app)[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{getUserName(app)}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{formatDateShort(app.created_at)}</Typography>}
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" color="success" onClick={() => handleApproveApplication(app.id)} title="Принять"><CheckCircleIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleRejectApplication(app.id)} title="Отклонить"><CancelIcon fontSize="small" /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )
              )}
            </Paper>
          )}

          {/* Organizer */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Организатор</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: '#1565c0', fontSize: '0.9rem' }}>
                {event.organizer_display_name?.[0] || 'О'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={700}>{event.organizer_display_name}</Typography>
                  {(event.organizer_code || event.organizer_login) && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        ({event.organizer_login ? `@${event.organizer_login}` : `#${event.organizer_code}`})
                      </Typography>
                      <IconButton size="small" onClick={async () => {
                        const idText = event.organizer_login || event.organizer_code || '';
                        try { await navigator.clipboard.writeText(idText); showSnackbar('Скопировано', 'success'); } catch {}
                      }}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar — actions for non-organizers */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, position: { md: 'sticky' }, top: { md: 80 } }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Действия</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {isOrganizer ? (
                <>
                  {/* Stats for organizer */}
                  <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>Статистика</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Свободных мест:</Typography>
                      <Typography variant="caption" fontWeight={700}>{event.max_participants - participantsCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Заполненность:</Typography>
                      <Typography variant="caption" fontWeight={700}>{Math.round((participantsCount / event.max_participants) * 100)}%</Typography>
                    </Box>
                    {event.created_at && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Опубликовано:</Typography>
                        <Typography variant="caption">{new Date(event.created_at).toLocaleDateString('ru-RU')}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мои мероприятия
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              ) : event.user_role === 'participant' ? (
                <>
                  <Button variant="outlined" color="error" fullWidth startIcon={<CancelIcon />} onClick={handleLeaveEvent}>
                    Отменить запись
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ChatIcon />} onClick={() => navigate(`/chats/${id}`)}>
                    Чат мероприятия
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мероприятия организатора
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              ) : event.user_role === 'applicant' ? (
                <>
                  <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>Заявка на рассмотрении</Alert>
                  <Button variant="outlined" color="warning" fullWidth onClick={handleCancelApplication}>
                    Отменить заявку
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мероприятия организатора
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              ) : isFull || event.status !== 'open' ? (
                <>
                  {isFull && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>Все места заняты</Alert>}
                  {event.status !== 'open' && <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>Регистрация закрыта</Alert>}
                  <Button variant={subscribed ? 'contained' : 'outlined'} fullWidth
                    startIcon={subscribed ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                    onClick={handleSubscribe}>
                    {subscribed ? 'Уведомления включены' : 'Уведомить о местах'}
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мероприятия организатора
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              ) : event.can_join ? (
                <>
                  <Button variant="contained" fullWidth onClick={() => handleJoinEvent()}
                    sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' }, py: 1.5, fontWeight: 700 }}>
                    {event.is_public ? 'Записаться' : 'Подать заявку'}
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мероприятия организатора
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outlined" fullWidth disabled>Регистрация закрыта</Button>
                  <Button variant="outlined" fullWidth startIcon={<EventIcon />}
                    onClick={() => navigate(`/events?organizer_id=${event.created_by}`)}>
                    Мероприятия организатора
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<ShareIcon />} onClick={handleShare}>
                    Поделиться
                  </Button>
                </>
              )}

              {joinCaptchaRequired && (
                <Box sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Проверка CAPTCHA</Typography>
                  <ReCaptchaWidget resetKey={joinCaptchaResetKey} onVerify={token => handleJoinEvent(token)} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Отмена</Button>
          <Button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog({ ...confirmDialog, open: false }); }} variant="contained" color="error">Подтвердить</Button>
        </DialogActions>
      </Dialog>

      {/* Reminder dialog */}
      <Dialog open={reminderDialog} onClose={() => setReminderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Напоминание участникам</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Будет отправлено {participants.length} участникам</Typography>
          <TextField fullWidth multiline rows={3} label="Текст (необязательно)" value={reminderMsg}
            onChange={e => setReminderMsg(e.target.value)} placeholder="Не забудьте взять удобную обувь!" size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialog(false)}>Отмена</Button>
          <Button onClick={handleSendReminder} variant="contained">Отправить</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventDetailPage;